import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiUpload, FiCheck } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';
import { formatPrice } from '../utils/constants';
import './PvcCard.css';

const ALLOWED_EXT = ['.jpg', '.jpeg', '.png', '.pdf', '.doc', '.docx', '.heic', '.heif'];
const MAX_SIZE_MB = 50;

const PvcCard = () => {
  const navigate = useNavigate();
  const { addToCart, cart } = useAuth();
  const fileInputFrontRef = useRef(null);
  const fileInputBackRef = useRef(null);

  const [services, setServices] = useState([
    { id: 'print', label: 'PVC Card Print', price: 80, description: 'Print on pre-existing PVC card (upload front & back)' },
    { id: 'lost_dl', label: 'Lost DL PVC Service', price: 200, description: 'Affidavit print on PVC for lost Driving License' },
    { id: 'lost_rc', label: 'Lost RC PVC Service', price: 200, description: 'Affidavit print on PVC for lost RC' },
    { id: 'lost_pan', label: 'Lost PAN Card PVC Service', price: 200, description: 'Affidavit print on PVC for lost PAN card' },
    { id: 'lost_vote', label: 'Lost Voter Card PVC Service', price: 200, description: 'Affidavit print on PVC for lost Voter ID' },
  ]);

  const [selectedService, setSelectedService] = useState('print');

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const { data } = await API.get('/print-rates');
        if (data.success && data.rates) {
          const find = (key, fallback) => data.rates.find(r => r.key === key)?.value ?? fallback;

          const pvcPrint   = find('pvc_print', 80);
          // Use specific per-card keys; fall back to the legacy pvc_lost if present, then to 200
          const pvcLostFallback = find('pvc_lost', 200);
          const pvcLostDl  = find('pvc_lost_dl',   pvcLostFallback);
          const pvcLostRc  = find('pvc_lost_rc',   pvcLostFallback);
          const pvcLostPan = find('pvc_lost_pan',  pvcLostFallback);
          const pvcLostVote= find('pvc_lost_vote', pvcLostFallback);

          setServices([
            { id: 'print',     label: 'PVC Card Print',          price: pvcPrint,    description: 'Print on pre-existing PVC card (upload front & back)' },
            { id: 'lost_dl',   label: 'Lost DL PVC Service',     price: pvcLostDl,   description: 'Affidavit print on PVC for lost Driving License' },
            { id: 'lost_rc',   label: 'Lost RC PVC Service',     price: pvcLostRc,   description: 'Affidavit print on PVC for lost RC' },
            { id: 'lost_pan',  label: 'Lost PAN Card PVC Service', price: pvcLostPan,  description: 'Affidavit print on PVC for lost PAN card' },
            { id: 'lost_vote', label: 'Lost Voter Card PVC Service', price: pvcLostVote, description: 'Affidavit print on PVC for lost Voter ID' },
          ]);
        }
      } catch (err) {
        console.warn('Failed to fetch PVC card rates:', err);
      }
    };
    fetchRates();
  }, []);
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [frontLocal, setFrontLocal] = useState(null);
  const [backLocal, setBackLocal] = useState(null);
  const [uploadingSide, setUploadingSide] = useState(null); // 'front' or 'back'
  const [docDetails, setDocDetails] = useState({
    dlNumber: '',
    dlDob: '',
    dlHolderName: '',
    vehicleNumber: '',
    chassisLast5: '',
    rcOwnerName: '',
    panNumber: '',
    panName: '',
    panDob: '',
    epicNumber: '',
    voterName: '',
    voterState: 'Haryana',
    additionalNotes: '',
  });
  const [addingToCart, setAddingToCart] = useState(false);

  const updateDocDetail = (key, val) => {
    setDocDetails(prev => ({ ...prev, [key]: val }));
  };

  const currentService = services.find(s => s.id === selectedService);

  const handleFileSelect = async (e, side) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const ext = '.' + file.name.split('.').pop().toLowerCase();
    if (!ALLOWED_EXT.includes(ext)) {
      toast.error(`Unsupported file type. Allowed: ${ALLOWED_EXT.join(', ')}`);
      return;
    }
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(`File too large. Max ${MAX_SIZE_MB}MB`);
      return;
    }

    // Set local preview
    const localUrl = URL.createObjectURL(file);
    if (side === 'front') {
      setFrontLocal(localUrl);
      setFrontFile(null);
    } else {
      setBackLocal(localUrl);
      setBackFile(null);
    }

    // Upload immediately
    setUploadingSide(side);
    try {
      const formData = new FormData();
      formData.append('files', file);
      const { data } = await API.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      // API returns { files: [{ ...cloudinary data }] }
      const uploaded = data.files[0];
      if (side === 'front') {
        setFrontFile(uploaded);
      } else {
        setBackFile(uploaded);
      }
      toast.success(`${side === 'front' ? 'Front' : 'Back'} image uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
      if (side === 'front') setFrontLocal(null);
      else setBackLocal(null);
    } finally {
      setUploadingSide(null);
    }
  };

  const validate = () => {
    if (selectedService === 'print') {
      if (!frontFile || !backFile) {
        toast.error('Please upload both front and back images');
        return false;
      }
    } else if (selectedService === 'lost_dl') {
      if (!docDetails.dlNumber.trim() || !docDetails.dlDob.trim()) {
        toast.error('Please enter DL Number and Date of Birth');
        return false;
      }
    } else if (selectedService === 'lost_rc') {
      if (!docDetails.vehicleNumber.trim()) {
        toast.error('Please enter Vehicle Registration Number');
        return false;
      }
    } else if (selectedService === 'lost_pan') {
      if (!docDetails.panNumber.trim() || !docDetails.panName.trim()) {
        toast.error('Please enter 10-digit PAN Number and Full Name');
        return false;
      }
    } else if (selectedService === 'lost_vote') {
      if (!docDetails.epicNumber.trim()) {
        toast.error('Please enter Voter EPIC Number');
        return false;
      }
    }
    return true;
  };

  const getFormattedLostSummary = () => {
    if (selectedService === 'lost_dl') {
      return `DL: ${docDetails.dlNumber.trim().toUpperCase()}, DOB: ${docDetails.dlDob.trim()}${docDetails.dlHolderName ? `, Name: ${docDetails.dlHolderName.trim()}` : ''}`;
    }
    if (selectedService === 'lost_rc') {
      return `RC: ${docDetails.vehicleNumber.trim().toUpperCase()}${docDetails.chassisLast5 ? `, Chassis(last 5): ${docDetails.chassisLast5.trim().toUpperCase()}` : ''}${docDetails.rcOwnerName ? `, Owner: ${docDetails.rcOwnerName.trim()}` : ''}`;
    }
    if (selectedService === 'lost_pan') {
      return `PAN: ${docDetails.panNumber.trim().toUpperCase()}, Name: ${docDetails.panName.trim()}${docDetails.panDob ? `, DOB: ${docDetails.panDob.trim()}` : ''}`;
    }
    if (selectedService === 'lost_vote') {
      return `Voter ID: ${docDetails.epicNumber.trim().toUpperCase()}${docDetails.voterName ? `, Name: ${docDetails.voterName.trim()}` : ''}, State: ${docDetails.voterState.trim()}`;
    }
    return '';
  };

  const hasValidDetails = selectedService === 'print'
    ? Boolean(frontFile && backFile)
    : selectedService === 'lost_dl'
    ? Boolean(docDetails.dlNumber.trim() && docDetails.dlDob.trim())
    : selectedService === 'lost_rc'
    ? Boolean(docDetails.vehicleNumber.trim())
    : selectedService === 'lost_pan'
    ? Boolean(docDetails.panNumber.trim() && docDetails.panName.trim())
    : selectedService === 'lost_vote'
    ? Boolean(docDetails.epicNumber.trim())
    : false;

  const isPvcInCart = Boolean(
    cart && cart.some((item) => {
      if (item.options?.pvcServiceId === selectedService) return true;
      if (item.options?.lostCardType === selectedService) return true;
      const name = (item.name || '').toLowerCase();
      const currentLabel = (services.find(s => s.id === selectedService)?.label || '').toLowerCase();
      if (currentLabel && name.includes(currentLabel)) return true;
      if (item.id && String(item.id).startsWith('pvc-card-')) return true;
      return false;
    })
  );

  const handleAddToCart = async () => {
    if (!validate()) return;

    setAddingToCart(true);
    try {
      const service = services.find(s => s.id === selectedService);
      const isPrint = selectedService === 'print';
      const structuredDetails = {
        serviceType: selectedService,
        ...(selectedService === 'lost_dl' && {
          dlNumber: docDetails.dlNumber.trim().toUpperCase(),
          dlDob: docDetails.dlDob.trim(),
          dlHolderName: docDetails.dlHolderName.trim()
        }),
        ...(selectedService === 'lost_rc' && {
          vehicleNumber: docDetails.vehicleNumber.trim().toUpperCase(),
          chassisLast5: docDetails.chassisLast5.trim().toUpperCase(),
          rcOwnerName: docDetails.rcOwnerName.trim()
        }),
        ...(selectedService === 'lost_pan' && {
          panNumber: docDetails.panNumber.trim().toUpperCase(),
          panName: docDetails.panName.trim(),
          panDob: docDetails.panDob.trim()
        }),
        ...(selectedService === 'lost_vote' && {
          epicNumber: docDetails.epicNumber.trim().toUpperCase(),
          voterName: docDetails.voterName.trim(),
          voterState: docDetails.voterState.trim()
        }),
        additionalNotes: docDetails.additionalNotes.trim()
      };

      const item = {
        id: `pvc-card-${Date.now()}`,
        type: 'mart',
        name: service.label,
        price: service.price,
        quantity: 1,
        image: isPrint ? (frontFile?.thumbnailLink || frontFile?.viewLink || frontLocal || null) : null,
        fileUrl: isPrint ? (frontFile?.viewLink || null) : null,
        options: {
          pvcServiceId: selectedService,
          service: service.label,
          lostCardType: isPrint ? null : selectedService,
          lostCardDetails: isPrint ? null : structuredDetails,
          lostDescription: isPrint ? '' : getFormattedLostSummary(),
          frontFile: isPrint ? (frontFile?.name || 'front.jpg') : '',
          backFile: isPrint ? (backFile?.name || 'back.jpg') : '',
          frontFileUrl: isPrint ? (frontFile?.viewLink || '') : '',
          backFileUrl: isPrint ? (backFile?.viewLink || '') : '',
        },
        attachments: isPrint ? [
          frontFile?.viewLink || frontFile?.thumbnailLink,
          backFile?.viewLink || backFile?.thumbnailLink,
        ].filter(Boolean) : [],
      };

      addToCart(item);
      toast.success(`${service.label} added to cart`);
      navigate('/checkout');
    } catch (err) {
      toast.error('Failed to add to cart');
    } finally {
      setAddingToCart(false);
    }
  };

  const handleClearFront = () => {
    setFrontFile(null);
    setFrontLocal(null);
    if (fileInputFrontRef.current) fileInputFrontRef.current.value = '';
  };

  const handleClearBack = () => {
    setBackFile(null);
    setBackLocal(null);
    if (fileInputBackRef.current) fileInputBackRef.current.value = '';
  };

  return (
    <div className="page pvc-page">
      {/* Header */}
      <div className="page-header">
        <button className="back-btn" onClick={() => navigate(-1)}>
          <FiArrowLeft size={20} />
        </button>
        <h1 className="page-title">PVC Card Services</h1>
      </div>

      {/* Service Selection */}
      <div className="pvc-service-list">
        {services.map(service => (
          <label key={service.id} className={`pvc-service-radio ${selectedService === service.id ? 'active' : ''}`}>
            <input
              type="radio"
              name="service"
              value={service.id}
              checked={selectedService === service.id}
              onChange={() => {
                setSelectedService(service.id);
                setFrontFile(null);
                setBackFile(null);
                setFrontLocal(null);
                setBackLocal(null);
              }}
            />
            <div className="pvc-service-info">
              <div className="pvc-service-name">{service.label}</div>
              <div className="pvc-service-desc">{service.description}</div>
            </div>
            <div className="pvc-service-price">{formatPrice(service.price)}</div>
          </label>
        ))}
      </div>

      {/* Upload Section (only for print) */}
      {selectedService === 'print' && (
        <div className="pvc-upload-section">
          <h3>Upload Card Images</h3>
          <div className="pvc-upload-grid">
            {/* Front */}
            <div className="pvc-upload-zone">
              <div className="pvc-upload-label">Front</div>
              {!frontFile ? (
                <div className="pvc-upload-empty" onClick={() => fileInputFrontRef.current?.click()}>
                  {uploadingSide === 'front' ? (
                    <div className="spinner" style={{width: 24, height: 24, borderWidth: 3}}></div>
                  ) : (
                    <>
                      <FiUpload size={32} />
                      <span>Tap to upload</span>
                    </>
                  )}
                </div>
              ) : (
                <div className="pvc-upload-preview">
                  <img src={frontLocal || frontFile.thumbnailLink} alt="Front" />
                  <button className="pvc-upload-clear" onClick={handleClearFront}>✕</button>
                </div>
              )}
              <input
                ref={fileInputFrontRef}
                type="file"
                accept={ALLOWED_EXT.join(',')}
                onChange={e => handleFileSelect(e, 'front')}
                style={{ display: 'none' }}
              />
            </div>

            {/* Back */}
            <div className="pvc-upload-zone">
              <div className="pvc-upload-label">Back</div>
              {!backFile ? (
                <div className="pvc-upload-empty" onClick={() => fileInputBackRef.current?.click()}>
                  {uploadingSide === 'back' ? (
                    <div className="spinner" style={{width: 24, height: 24, borderWidth: 3}}></div>
                  ) : (
                    <>
                      <FiUpload size={32} />
                      <span>Tap to upload</span>
                    </>
                  )}
                </div>
              ) : (
                <div className="pvc-upload-preview">
                  <img src={backLocal || backFile.thumbnailLink} alt="Back" />
                  <button className="pvc-upload-clear" onClick={handleClearBack}>✕</button>
                </div>
              )}
              <input
                ref={fileInputBackRef}
                type="file"
                accept={ALLOWED_EXT.join(',')}
                onChange={e => handleFileSelect(e, 'back')}
                style={{ display: 'none' }}
              />
            </div>
          </div>
          <div className="pvc-upload-hint">Upload clear images of front and back of your card</div>
        </div>
      )}

      {/* Lost Card Structured Verification Inputs */}
      {selectedService.startsWith('lost_') && (
        <div className="pvc-description-section" style={{ background: 'var(--bg-card)', padding: '16px', borderRadius: '12px', border: '1px solid var(--border)', marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--primary)', fontWeight: '700', fontSize: '1.05rem', marginBottom: '4px' }}>
            <span>🏛️</span>
            <span>
              {selectedService === 'lost_dl' && 'Driving License Details (Parivahan Sarathi)'}
              {selectedService === 'lost_rc' && 'Vehicle RC Details (Parivahan Vahan)'}
              {selectedService === 'lost_pan' && 'PAN Card Details (Income Tax Dept)'}
              {selectedService === 'lost_vote' && 'Voter ID Details (Election Commission)'}
            </span>
          </div>
          <p style={{ fontSize: '0.82rem', color: 'var(--text-secondary)', marginBottom: '16px' }}>
            Enter official details for Bharat Sarkar verification and authentic PVC re-issue.
          </p>

          {selectedService === 'lost_dl' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Driving License Number *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. HR14 20180001234"
                  value={docDetails.dlNumber}
                  onChange={e => updateDocDetail('dlNumber', e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Date of Birth (DD/MM/YYYY) *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="DD/MM/YYYY"
                  value={docDetails.dlDob}
                  onChange={e => updateDocDetail('dlDob', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Full Name (as on DL)</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Full Name as per license"
                  value={docDetails.dlHolderName}
                  onChange={e => updateDocDetail('dlHolderName', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
          )}

          {selectedService === 'lost_rc' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Vehicle Registration Number *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. HR14AB1234"
                  value={docDetails.vehicleNumber}
                  onChange={e => updateDocDetail('vehicleNumber', e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Chassis Number (Last 5 digits)</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. 54321 (optional helper)"
                  value={docDetails.chassisLast5}
                  onChange={e => updateDocDetail('chassisLast5', e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Registered Owner Name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Owner Name as per Vahan records"
                  value={docDetails.rcOwnerName}
                  onChange={e => updateDocDetail('rcOwnerName', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
          )}

          {selectedService === 'lost_pan' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>10-Digit PAN Number *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. ABCDE1234F"
                  maxLength={10}
                  value={docDetails.panNumber}
                  onChange={e => updateDocDetail('panNumber', e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Full Name (as per PAN Card) *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Full Name as per Income Tax records"
                  value={docDetails.panName}
                  onChange={e => updateDocDetail('panName', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Date of Birth (DD/MM/YYYY)</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="DD/MM/YYYY"
                  value={docDetails.panDob}
                  onChange={e => updateDocDetail('panDob', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
          )}

          {selectedService === 'lost_vote' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>EPIC / Voter ID Number *</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="e.g. ABC1234567 or HR/02/012/123456"
                  value={docDetails.epicNumber}
                  onChange={e => updateDocDetail('epicNumber', e.target.value.toUpperCase())}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>Elector Full Name</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Name as registered in voter roll"
                  value={docDetails.voterName}
                  onChange={e => updateDocDetail('voterName', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: '600', marginBottom: '4px', color: 'var(--text-primary)' }}>State / UT</label>
                <input
                  type="text"
                  className="input-field"
                  placeholder="Haryana"
                  value={docDetails.voterState}
                  onChange={e => updateDocDetail('voterState', e.target.value)}
                  style={{ width: '100%', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border)', background: 'var(--bg-input)', color: 'var(--text-primary)' }}
                />
              </div>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '14px', padding: '10px', borderRadius: '8px', background: 'rgba(16, 185, 129, 0.08)', border: '1px solid rgba(16, 185, 129, 0.25)', color: '#10B981', fontSize: '0.8rem' }}>
            <FiCheck size={16} />
            <span>Encrypted & verified internally via Bharat Sarkar Digital Portal. Visible only to admin for authentic PVC printing.</span>
          </div>
        </div>
      )}

      {/* Summary */}
      <div className="pvc-summary">
        <div className="pvc-summary-row">
          <span>Service</span>
          <span>{currentService.label}</span>
        </div>
        <div className="pvc-summary-row total">
          <span>Total</span>
          <span className="pvc-total-price">{formatPrice(currentService.price)}</span>
        </div>
      </div>

      {/* Add / View in Cart Buttons */}
      {isPvcInCart && !hasValidDetails ? (
        <button
          className="btn btn-primary btn-full pvc-add-btn"
          onClick={() => navigate('/checkout')}
          style={{ marginTop: 16 }}
        >
          <FiCheck size={18} />
          View in Cart
        </button>
      ) : isPvcInCart ? (
        <div style={{ display: 'flex', gap: '10px', marginTop: 16 }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/checkout')}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              border: '1.5px solid var(--primary, #6366f1)',
              color: 'var(--primary, #6366f1)',
              background: 'transparent',
              borderRadius: '8px',
              fontWeight: 700
            }}
          >
            View in Cart
          </button>
          <button
            className="btn btn-primary pvc-add-btn"
            onClick={handleAddToCart}
            disabled={addingToCart || (selectedService === 'print' && uploadingSide)}
            style={{ flex: 2 }}
          >
            {addingToCart ? 'Adding...' : (
              <>
                <FiCheck size={18} />
                Add to Cart • {formatPrice(currentService.price)}
              </>
            )}
          </button>
        </div>
      ) : (
        <button
          className="btn btn-primary btn-full pvc-add-btn"
          onClick={handleAddToCart}
          disabled={addingToCart || (selectedService === 'print' && uploadingSide)}
          style={{ marginTop: 16 }}
        >
          {addingToCart ? (
            'Adding...'
          ) : (
            <>
              <FiCheck size={18} />
              Add to Cart • {formatPrice(currentService.price)}
            </>
          )}
        </button>
      )}
    </div>
  );
};

export default PvcCard;
